import { defineStore } from 'pinia'
import { supabase } from '@/supabase'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    profile: null,
    tokens: 0,
    notifications: [],
    messageCount: 0,
    requestCount: 0,
    channels: {}
  }),
  getters: {
    unreadCount: (state) => state.notifications.length,
    isAdmin: (state) => state.user?.id === "c018e367-c227-4633-821a-9a8a13b7a352",
    fullName: (state) => state.profile?.full_name || ""
  },
  actions: {
    async initialize() {
      const { data: { session } } = await supabase.auth.getSession()
      this.user = session?.user || null

      supabase.auth.onAuthStateChange((_event, session) => {
        this.user = session?.user || null
        if (this.user) {
          this.fetchInitialData()
        } else {
          this.clearData()
        }
      })

      if (this.user) {
        await this.fetchInitialData()
      }
    },

    async fetchInitialData() {
      await Promise.all([
        this.fetchProfile(),
        this.fetchTokens(),
        this.fetchNotifications(),
        this.fetchMessageCount(),
        this.fetchRequestCount()
      ])
      this.setupRealtime()
    },

    clearData() {
      this.profile = null
      this.tokens = 0
      this.notifications = []
      this.messageCount = 0
      this.requestCount = 0
      this.cleanupRealtime()
    },

    async fetchProfile() {
      const { data } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('id', this.user.id)
        .single()
      this.profile = data
    },

    async fetchTokens() {
      const { data } = await supabase
        .from('user_profiles')
        .select('mudra_tokens')
        .eq('id', this.user.id)
        .single()
      this.tokens = data?.mudra_tokens || 0
    },

    async fetchNotifications() {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('receiver_id', this.user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
      this.notifications = data || []
    },

    async fetchMessageCount() {
      const { data } = await supabase
        .from('messages')
        .select('sender_id')
        .eq('receiver_id', this.user.id)
        .eq('is_read', false)
      const uniqueUsers = new Set()
      data?.forEach(msg => uniqueUsers.add(msg.sender_id))
      this.messageCount = uniqueUsers.size
    },

    async fetchRequestCount() {
      const { data } = await supabase
        .from('connections')
        .select('id')
        .eq('receiver_id', this.user.id)
        .eq('status', 'pending')
      this.requestCount = data?.length || 0
    },

    async markNotificationRead(id) {
      await supabase.from("notifications").update({ is_read: true }).eq("id", id)
      this.notifications = this.notifications.filter(n => n.id !== id)
    },

    setupRealtime() {
      this.cleanupRealtime()
      
      this.channels.tokens = supabase.channel(`tokens-${this.user.id}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${this.user.id}` }, (payload) => {
          this.tokens = payload.new.mudra_tokens
        }).subscribe()

      this.channels.notifications = supabase.channel(`realtime:notifications:${this.user.id}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `receiver_id=eq.${this.user.id}` }, (payload) => {
          this.notifications.unshift(payload.new)
        }).subscribe()

      this.channels.messages = supabase.channel('navbar-messages')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, (payload) => {
          if (payload.new?.receiver_id === this.user.id || payload.old?.receiver_id === this.user.id) {
            this.fetchMessageCount()
          }
        }).subscribe()

      this.channels.requests = supabase.channel('navbar-requests')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'connections' }, (payload) => {
          if (payload.new?.receiver_id === this.user.id || payload.old?.receiver_id === this.user.id) {
            this.fetchRequestCount()
          }
        }).subscribe()
    },

    cleanupRealtime() {
      Object.values(this.channels).forEach(channel => {
        if (channel) supabase.removeChannel(channel)
      })
      this.channels = {}
    }
  }
})
